import { Building2, TrendingUp, Percent, Euro } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

// KPI Card Component
interface KPICardProps {
  title: string;
  value: string;
  change?: string;
  changeType?: "positive" | "negative" | "neutral";
  icon: React.ReactNode;
  iconBg: string;
}

function KPICard({
  title,
  value,
  change,
  changeType = "neutral",
  icon,
  iconBg,
}: KPICardProps) {
  return (
    <Card className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm text-gray-600 mb-2">{title}</p>
            <p className="text-3xl mb-1" style={{ fontWeight: 600 }}>
              {value}
            </p>
            {change && (
              <div className="flex items-center gap-1">
                <span
                  className={`text-sm ${
                    changeType === "positive"
                      ? "text-[#10B981]"
                      : changeType === "negative"
                      ? "text-[#EF4444]"
                      : "text-gray-600"
                  }`}
                  style={{ fontWeight: 500 }}
                >
                  {change}
                </span>
                <span className="text-xs text-gray-500">vs mes anterior</span>
              </div>
            )}
          </div>
          <div
            className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}
          >
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Sample Data
const activos = [
  {
    id: 1,
    nombre: "Edificio La Unión",
    ubicacion: "Málaga, España",
    rentasAnuales: "€109.000",
    valorActual: "€1.990.000",
    rentabilidad: "9.3%",
    estado: "activo",
    imagen:
      "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400&h=300&fit=crop",
  },
  {
    id: 2,
    nombre: "Camas Residencial",
    ubicacion: "Sevilla, España",
    rentasAnuales: "€64.000",
    valorActual: "€890.000",
    rentabilidad: "7.2%",
    estado: "estabilizando",
    imagen:
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop",
  },
  {
    id: 3,
    nombre: "Torre Financiera",
    ubicacion: "Madrid, España",
    rentasAnuales: "€180.000",
    valorActual: "€2.450.000",
    rentabilidad: "8.7%",
    estado: "activo",
    imagen:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop",
  },
  {
    id: 4,
    nombre: "Plaza Comercial",
    ubicacion: "Valencia, España",
    rentasAnuales: "€52.000",
    valorActual: "€720.000",
    rentabilidad: "6.8%",
    estado: "activo",
    imagen:
      "https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=400&h=300&fit=crop",
  },
];

export function AdminDashboardView() {
  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1
          className="text-3xl text-[#0F172A] mb-2"
          style={{ fontWeight: 600 }}
        >
          Dashboard
        </h1>
        <p className="text-gray-600">
          Vista general de activos y rendimiento del portfolio
        </p>
      </div>

      {/* KPIs Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Activos gestionados"
          value="17"
          change="+2"
          changeType="positive"
          icon={<Building2 size={24} className="text-[#2B59FF]" />}
          iconBg="bg-[#2B59FF]/10"
        />
        <KPICard
          title="NAV total"
          value="€4.820.000"
          change="+2,4%"
          changeType="positive"
          icon={<Euro size={24} className="text-[#10B981]" />}
          iconBg="bg-[#10B981]/10"
        />
        <KPICard
          title="Ocupación media"
          value="92%"
          change="+1,2%"
          changeType="positive"
          icon={<Percent size={24} className="text-[#F59E0B]" />}
          iconBg="bg-[#F59E0B]/10"
        />
        <KPICard
          title="Renta anualizada"
          value="€243.000"
          change="+3,8%"
          changeType="positive"
          icon={<TrendingUp size={24} className="text-[#8B5CF6]" />}
          iconBg="bg-[#8B5CF6]/10"
        />
      </div>

      {/* Assets Table Section */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2
              className="text-xl text-[#0F172A] mb-1"
              style={{ fontWeight: 600 }}
            >
              Lista de activos
            </h2>
            <p className="text-sm text-gray-600">
              Gestión completa del portfolio inmobiliario
            </p>
          </div>
          {/*  <div className="flex items-center gap-3">
            <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
              Filtros
            </Button>
            <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
              Exportar
            </Button>
          </div> */}
        </div>

        <Card className="border border-gray-200 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th
                    className="text-left px-6 py-4 text-xs text-gray-600 uppercase tracking-wider"
                    style={{ fontWeight: 600 }}
                  >
                    Activo
                  </th>
                  <th
                    className="text-left px-6 py-4 text-xs text-gray-600 uppercase tracking-wider"
                    style={{ fontWeight: 600 }}
                  >
                    Ubicación
                  </th>
                  <th
                    className="text-left px-6 py-4 text-xs text-gray-600 uppercase tracking-wider"
                    style={{ fontWeight: 600 }}
                  >
                    Rentas/año
                  </th>
                  <th
                    className="text-left px-6 py-4 text-xs text-gray-600 uppercase tracking-wider"
                    style={{ fontWeight: 600 }}
                  >
                    Valor actual
                  </th>
                  <th
                    className="text-left px-6 py-4 text-xs text-gray-600 uppercase tracking-wider"
                    style={{ fontWeight: 600 }}
                  >
                    Rentabilidad
                  </th>
                  <th
                    className="text-left px-6 py-4 text-xs text-gray-600 uppercase tracking-wider"
                    style={{ fontWeight: 600 }}
                  >
                    Estado
                  </th>
                  <th
                    className="text-left px-6 py-4 text-xs text-gray-600 uppercase tracking-wider"
                    style={{ fontWeight: 600 }}
                  >
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {activos.map((activo) => (
                  <tr
                    key={activo.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer group"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={activo.imagen}
                          alt={activo.nombre}
                          className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                        />
                        <div>
                          <p
                            className="text-sm text-[#0F172A]"
                            style={{ fontWeight: 500 }}
                          >
                            {activo.nombre}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-700">
                        {activo.ubicacion}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className="text-sm text-gray-900"
                        style={{ fontWeight: 500 }}
                      >
                        {activo.rentasAnuales}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className="text-sm text-gray-900"
                        style={{ fontWeight: 500 }}
                      >
                        {activo.valorActual}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className="text-sm text-[#10B981]"
                        style={{ fontWeight: 600 }}
                      >
                        {activo.rentabilidad}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Badge
                        className={
                          activo.estado === "activo"
                            ? "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20"
                            : "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20"
                        }
                      >
                        {activo.estado === "activo"
                          ? "🟢 Activo"
                          : "🟡 Estabilizando"}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[#2B59FF] hover:text-[#2347CC] hover:bg-[#2B59FF]/10"
                        >
                          Editar
                        </Button>
                        <span className="text-gray-300">·</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                        >
                          Ver
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
